import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Auto-add product to cart functionality
 * Automatically adds a specified product (like shipping protection) whenever any product is added to the cart
 */
class AutoAddProduct {
  constructor() {
    this.variantId = window.Shopify?.autoAddProduct?.variantId;
    this.enabled = window.Shopify?.autoAddProduct?.enabled === 'true' || window.Shopify?.autoAddProduct?.enabled === true;
    this.isAdding = false; // Separate flag for adding operations
    this.isChecking = false; // Separate flag for checking operations
    this.userRemovedProtection = false; // Track if user manually removed the protection
    this.justRemovedForSafety = false; // Track if we just removed it for safety - prevents re-adding
    this.lastNonProtectionItemCount = 0; // Track cart size (excluding protection) to detect real new products
    
    // Debug logging
    console.log('[Auto-Add Product] Configuration:', {
      enabled: this.enabled,
      variantId: this.variantId,
      rawEnabled: window.Shopify?.autoAddProduct?.enabled,
    });
    
    if (this.enabled && this.variantId && this.variantId !== '') {
      console.log('[Auto-Add Product] Initializing...');
      this.init();
    } else {
      console.log('[Auto-Add Product] Not initialized - missing configuration');
    }
  }

  init() {
    console.log('[Auto-Add Product] Listening for cart additions and updates');
    document.addEventListener(CartAddEvent.eventName, this.handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }

  /**
   * Handle cart add event
   * @param {CartAddEvent} event
   */
  handleCartAdd = async (event) => {
    console.log('[Auto-Add Product] Cart add event detected:', event.detail);
    
    // CRITICAL: Don't add if we just removed it for safety
    if (this.justRemovedForSafety) {
      console.log('[Auto-Add Product] 🛑 BLOCKED - We just removed it for safety, not adding back!');
      return;
    }
    
    // Don't add if already adding
    if (this.isAdding) {
      console.log('[Auto-Add Product] Skipping - already adding');
      return;
    }
    
    if (event.detail.data?.didError) {
      console.log('[Auto-Add Product] Skipping - original add had error');
      return;
    }

    // Don't add if this event was triggered by the auto-add itself to avoid infinite loop
    if (event.detail.data?.source === 'auto-add-product') {
      console.log('[Auto-Add Product] Skipping - this is our own add event');
      return;
    }

    try {
      this.isAdding = true;
      
      // CRITICAL SAFETY CHECK: Check the actual cart state first
      // This prevents adding protection to an empty cart or when only protection exists
      const currentCart = await this.getCart();
      console.log('[Auto-Add Product] Current cart has', currentCart.items.length, 'items');
      
      // If cart is empty or only has protection, DON'T add
      if (currentCart.items.length === 0) {
        console.log('[Auto-Add Product] 🛑 Cart is empty - not adding protection to empty cart');
        return;
      }
      
      // Check if cart only has our protection product
      const protectionVariantId = parseInt(this.variantId || '0');
      const onlyHasProtection = currentCart.items.length === 1 && 
                                currentCart.items[0]?.variant_id === protectionVariantId;
      
      if (onlyHasProtection) {
        console.log('[Auto-Add Product] 🛑 Cart only has protection - not adding it back');
        this.justRemovedForSafety = true;
        return;
      }
      
      // Count items that are NOT the protection product
      const nonProtectionItems = currentCart.items.filter(item => item.variant_id !== protectionVariantId);
      const currentNonProtectionCount = nonProtectionItems.length;
      
      console.log('[Auto-Add Product] Non-protection items:', currentNonProtectionCount, 'Previous:', this.lastNonProtectionItemCount);
      
      // Only reset userRemovedProtection if a REAL NEW product was added (count increased)
      if (currentNonProtectionCount > this.lastNonProtectionItemCount) {
        console.log('[Auto-Add Product] ✅ Real new product detected - cart grew from', this.lastNonProtectionItemCount, 'to', currentNonProtectionCount);
        
        if (this.userRemovedProtection) {
          console.log('[Auto-Add Product] Resetting user removal flag - new product added');
          this.userRemovedProtection = false;
        }
        
        if (this.justRemovedForSafety) {
          console.log('[Auto-Add Product] Resetting safety removal flag - new product added');
          this.justRemovedForSafety = false;
        }
      } else {
        console.log('[Auto-Add Product] No new products added, respecting previous user choice');
      }
      
      // Update the count for next time
      this.lastNonProtectionItemCount = currentNonProtectionCount;
      
      // CRITICAL: If user manually removed protection, respect their choice
      if (this.userRemovedProtection) {
        console.log('[Auto-Add Product] 🛑 User manually removed protection - NOT adding it back');
        return;
      }
      
      console.log('[Auto-Add Product] Checking if product is already in cart...');

      // Check if the auto-add product is already in the cart
      const isAlreadyInCart = currentCart.items.some(item => item.variant_id === protectionVariantId);
      
      if (!isAlreadyInCart) {
        console.log('[Auto-Add Product] Product not in cart, adding now...');
        await this.addProductToCart();
      } else {
        console.log('[Auto-Add Product] Product already in cart, skipping');
      }
    } catch (error) {
      console.error('[Auto-Add Product] Error:', error);
    } finally {
      this.isAdding = false;
    }
  };

  /**
   * Handle cart update event
   * Checks for two scenarios:
   * 1. User manually removed the protection - respect their choice
   * 2. Only protection remains in cart - remove it automatically
   * @param {CartUpdateEvent} event
   */
  handleCartUpdate = async (event) => {
    // Don't process our own events to avoid loops
    if (event.detail.data?.source === 'auto-add-product' || event.detail.data?.source === 'auto-add-product-removal') {
      console.log('[Auto-Add Product] Skipping - this is our own event');
      return;
    }

    // Don't process if we're already checking (avoid duplicate checks)
    if (this.isChecking) {
      console.log('[Auto-Add Product] Skipping update - already checking');
      return;
    }

    if (!this.variantId) {
      console.log('[Auto-Add Product] No variant ID configured');
      return;
    }

    try {
      this.isChecking = true;
      console.log('[Auto-Add Product] Cart updated, checking state...');

      const cart = await this.getCart();
      const protectionVariantId = parseInt(this.variantId);
      
      // Check if protection product exists in cart
      const protectionInCart = cart.items.some(item => item.variant_id === protectionVariantId);
      
      // Update the non-protection item count
      const nonProtectionItems = cart.items.filter(item => item.variant_id !== protectionVariantId);
      this.lastNonProtectionItemCount = nonProtectionItems.length;
      
      // SCENARIO 1: User manually removed the protection
      // If protection was in cart before but now it's not, and there are other items
      if (!protectionInCart && cart.items.length > 0) {
        console.log('[Auto-Add Product] User removed protection manually - will not re-add');
        this.userRemovedProtection = true;
      }
      
      // SCENARIO 2: CRITICAL - Only protection remains in cart
      // NEVER ALLOW CHECKOUT WITH ONLY PROTECTION
      if (cart.items.length === 1 && protectionInCart) {
        const onlyItem = cart.items[0];
        if (onlyItem) {
          console.log('[Auto-Add Product] ⚠️ CRITICAL: Only shipping protection in cart - REMOVING IT NOW!');
          this.justRemovedForSafety = true; // Set flag to prevent immediate re-add
          await this.removeProductFromCart(onlyItem.key);
        }
      } else if (cart.items.length > 1 && protectionInCart) {
        console.log('[Auto-Add Product] Cart has', cart.items.length, 'items including protection - OK');
        this.justRemovedForSafety = false; // Reset flag when cart is in good state
      } else if (cart.items.length === 0) {
        console.log('[Auto-Add Product] Cart is empty');
        this.userRemovedProtection = false; // Reset flag when cart is empty
        this.justRemovedForSafety = false; // Reset safety flag too
      }
    } catch (error) {
      console.error('[Auto-Add Product] Error in cart update handler:', error);
    } finally {
      this.isChecking = false;
    }
  };

  /**
   * Get current cart
   * @returns {Promise<{items: Array<{variant_id: number, key: string}>, item_count: number}>}
   */
  async getCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }

    return await response.json();
  }

  /**
   * Check if the auto-add product is already in the cart
   * @returns {Promise<boolean>}
   */
  async isProductInCart() {
    try {
      if (!this.variantId) return false;
      
      const cart = await this.getCart();
      const variantId = this.variantId; // Store for type safety
      
      // Check if any cart item has the same variant ID
      return cart.items.some(item => item.variant_id === parseInt(variantId));
    } catch (error) {
      console.error('Error checking cart:', error);
      return false;
    }
  }

  /**
   * Add the auto-add product to cart
   */
  async addProductToCart() {
    try {
      if (!this.variantId) {
        console.error('[Auto-Add Product] No variant ID to add');
        return;
      }
      
      console.log('[Auto-Add Product] Adding variant ID:', this.variantId);
      
      const formData = new FormData();
      formData.append('id', this.variantId);
      formData.append('quantity', '1');

      // Get all cart items components to update
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      /** @type {string[]} */
      const cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      });
      
      if (cartItemComponentsSectionIds.length > 0) {
        formData.append('sections', cartItemComponentsSectionIds.join(','));
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });

      console.log('[Auto-Add Product] Sending request to:', Theme.routes.cart_add_url);
      
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });

      const result = await response.json();
      console.log('[Auto-Add Product] Response:', result);

      if (result.status) {
        // Error occurred
        console.error('[Auto-Add Product] Failed:', result.message, result);
        return;
      }

      console.log('[Auto-Add Product] Successfully added to cart!');

      // Dispatch a custom CartAddEvent for the auto-added product
      // This will trigger cart updates but won't trigger another auto-add (due to source check)
      document.dispatchEvent(
        new CartAddEvent({}, this.variantId, {
          source: 'auto-add-product',
          itemCount: 1,
          sections: result.sections,
        })
      );

    } catch (error) {
      console.error('[Auto-Add Product] Error adding to cart:', error);
    }
  }

  /**
   * Remove the auto-add product from cart
   * @param {string} lineItemKey - The cart line item key
   */
  async removeProductFromCart(lineItemKey) {
    try {
      console.log('[Auto-Add Product] Removing product with key:', lineItemKey);

      const body = JSON.stringify({
        updates: {
          [lineItemKey]: 0
        }
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body,
      });

      if (!response.ok) {
        throw new Error('Failed to remove product from cart');
      }

      const result = await response.json();
      console.log('[Auto-Add Product] ✅ Successfully removed from cart!');

      // Dispatch cart update event to refresh the UI
      document.dispatchEvent(
        new CartUpdateEvent({}, 'auto-add-product-removal', {
          source: 'auto-add-product-removal',
          itemCount: result.item_count,
        })
      );

    } catch (error) {
      console.error('[Auto-Add Product] Error removing from cart:', error);
    }
  }

  destroy() {
    document.removeEventListener(CartAddEvent.eventName, this.handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate);
  }
}

// Initialize the auto-add product functionality
let autoAddProductInstance;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    autoAddProductInstance = new AutoAddProduct();
  });
} else {
  autoAddProductInstance = new AutoAddProduct();
}

export default AutoAddProduct;

